import { Channel } from 'src/broadcast/Channels';
import { Client } from 'src/broadcast/Client';
import {
  NotificationCreatedEvent,
  RequestUpdatedByEditorEvent,
} from 'src/broadcast/Events/Company';

import { buildUrl } from 'src/helpers';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
import UserGuidingUserPayloadPublished from 'src/broadcast/Events/UserGuidingUserPayloadPublished';
import { Logger } from 'src/utils/logger';

class UserChannel extends Channel {
  private channel?: string = undefined;

  public readonly channelName = 'user.{user}';

  public notificationCreated: NotificationCreatedEvent;
  public requestUpdatedByEditor: RequestUpdatedByEditorEvent;
  public userGuidingUserPayloadPublished: UserGuidingUserPayloadPublished;

  constructor(client: Client) {
    super(client);

    this.notificationCreated = new NotificationCreatedEvent();
    this.requestUpdatedByEditor = new RequestUpdatedByEditorEvent();
    this.userGuidingUserPayloadPublished = new UserGuidingUserPayloadPublished();
  }

  public connect(channel: string): this {
    if (AuthUtils.isExpired() || this.isConnected()) {
      return this;
    }

    try {
      const payload = AuthUtils.getPayload();
      if (!payload?.sub) {
        Logger.warn('Cannot connect to user channel: no user ID in JWT payload', 'UserChannel');
        return this;
      }

      this.client.setBearer(channel);

      const { sub: user } = payload;
      this.channel = buildUrl(this.channelName, { user });
      const room = this.client.echo.private(this.channel);

      this.notificationCreated.setChannel(room).listen();
      this.requestUpdatedByEditor.setChannel(room).listen();
      this.userGuidingUserPayloadPublished.setChannel(room).listen();

      this.setIsConnected(true);
    } catch (error) {
      // Gracefully handle Pusher/Echo connection errors
      // These can occur during handshake or when the connection is in an invalid state
      Logger.warn('Error connecting to user channel', 'UserChannel', error);
    }

    return this;
  }

  public disconnect(): this {
    try {
      this.notificationCreated.removeChannel().close();
      this.requestUpdatedByEditor.removeChannel().close();
      this.userGuidingUserPayloadPublished.removeChannel().close();

      if (this.channel) {
        this.client.echo.leave(this.channel);
        this.channel = undefined;
      }
    } catch (error) {
      // Gracefully handle Pusher/Echo disconnection errors
      // These can occur when the connection is already closed or in an invalid state
      Logger.warn('Error disconnecting from user channel', 'UserChannel', error);
      this.channel = undefined;
    }

    this.setIsConnected(false);

    return this;
  }
}

export default UserChannel;
